import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getMagazines } from '../services/api';
import { BookOpen, Calendar, ExternalLink } from 'lucide-react';
import MagazineViewer from '../components/MagazineViewer';

const MagazinePage = () => {
    const [magazines, setMagazines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMagazine, setSelectedMagazine] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchMags = async () => {
            try {
                const data = await getMagazines();
                setMagazines(data);
            } catch (error) {
                console.error("Dergiler çekilemedi:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchMags();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-modern-blue-50 flex items-center justify-center">
                <div className="text-xl font-bold text-cyan-600">Yükleniyor...</div>
            </div>
        );
    }

    return (
        <div className="bg-modern-blue-50 text-modern-blue-900 min-h-screen">
            <main className="pt-32 pb-64 px-6 relative z-0">
                <div className="container mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-modern-blue-900 via-modern-blue-600 to-emerald-500 mb-6 drop-shadow-sm font-heading">
                            Dergimiz
                        </h1>
                        <p className="text-modern-blue-600 max-w-2xl mx-auto text-lg leading-relaxed">
                            TURET Dergisi'nin tüm sayılarına buradan ulaşabilirsiniz.
                        </p>
                    </motion.div>

                    {magazines.length === 0 ? (
                        <div className="text-center text-gray-500 py-12">
                            Henüz yayınlanmış bir dergi bulunmuyor.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 justify-center">
                            {magazines.map((mag, idx) => (
                                <MagazineCard
                                    key={mag.id}
                                    magazine={mag}
                                    index={idx}
                                    onRead={() => setSelectedMagazine(mag)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Flipbook Viewer Modal */}
            <MagazineViewer
                isOpen={!!selectedMagazine}
                onClose={() => setSelectedMagazine(null)}
                pages={selectedMagazine?.pages || []}
            />
        </div>
    );
};

const MagazineCard = ({ magazine, index, onRead }) => {
    const hasPages = magazine.pages && magazine.pages.length > 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ y: -10 }}
            className="group flex flex-col items-center text-center w-full max-w-[300px] mx-auto"
        >
            {/* Cover Image Container */}
            <div
                className="relative w-full aspect-[1/1.414] rounded-lg shadow-[0_10px_20px_rgba(0,0,0,0.3)] group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] transition-all duration-300 transform group-hover:-translate-y-2 cursor-pointer overflow-hidden bg-gray-200"
                onClick={hasPages ? onRead : undefined}
            >
                {/* Book Spine Effect (Left) */}
                <div className="absolute left-0 top-0 bottom-0 w-1 sm:w-2 bg-gradient-to-r from-white/20 to-transparent z-20 pointer-events-none" />
                <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-black/10 z-20 pointer-events-none" />

                {magazine.coverImage ? (
                    <img
                        src={magazine.coverImage}
                        alt={magazine.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        style={{
                            backfaceVisibility: 'hidden',
                            transform: 'translateZ(0)',
                            WebkitFontSmoothing: 'subpixel-antialiased',
                            imageRendering: 'auto' // Let browser decide best interpolation
                        }}
                    />
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-2 bg-gray-100">
                        <BookOpen size={48} strokeWidth={1} />
                        <span className="text-sm font-medium">Kapak Görseli Yok</span>
                    </div>
                )}

                {/* Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </div>

            {/* Info Below Cover */}
            <div className="mt-6 space-y-2 w-full px-2">
                {/* Title */}
                <h3 className="text-xl font-bold text-modern-blue-900 leading-tight group-hover:text-cyan-600 transition-colors">
                    {magazine.title}
                </h3>

                {/* Date */}
                <div className="flex items-center justify-center gap-2 text-gray-500 text-sm font-medium">
                    <Calendar size={14} />
                    {magazine.date}
                </div>

                {/* Action Buttons */}
                <div className="pt-4 flex flex-col gap-2">
                    {hasPages ? (
                        <button
                            onClick={onRead}
                            className="bg-modern-blue-900 text-white font-bold py-2 px-6 rounded-full text-sm hover:bg-cyan-600 transition-colors shadow-md w-full"
                        >
                            Oku
                        </button>
                    ) : (
                        magazine.pdfUrl && (
                            <a
                                href={magazine.pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-gray-200 text-gray-700 font-bold py-2 px-6 rounded-full text-sm hover:bg-gray-300 transition-colors w-full flex items-center justify-center gap-2"
                            >
                                <ExternalLink size={14} />
                                PDF İndir
                            </a>
                        )
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default MagazinePage;
